import {
  getEnabledCountryLocationData,
  getLocationDisplayName,
} from '@/services/admin/locationData';
import { useIntl } from '@umijs/max';
import { GlobalOutlined, WarningOutlined } from '@ant-design/icons';
import { Popover, Space, Tag, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';

export interface RegionDisplayProps {
  /** 地区 code（或 code 数组），业务侧统一用 code 存储与匹配 */
  locationCodes: string | string[] | null | undefined;
  /** 空值时的显示文本（默认 '-'） */
  emptyText?: string;
  /** Tag 间距 */
  spaceSize?: number | [number, number];
  /** 是否换行 */
  wrap?: boolean;
}

function formatDisplayTextItems(
  displayText: Record<string, unknown> | undefined,
  langLabels: Record<string, string>,
): Array<{ lang: string; label: string; text: string }> {
  if (!displayText || typeof displayText !== 'object') return [];
  const items: Array<{ lang: string; label: string; text: string }> = [];
  ['zh_CN', 'en_US', 'km_KH'].forEach((lang) => {
    if (displayText[lang]) {
      items.push({
        lang,
        label: langLabels[lang] || lang,
        text: String(displayText[lang]),
      });
    }
  });
  Object.entries(displayText).forEach(([lang, text]) => {
    if (!['zh_CN', 'en_US', 'km_KH'].includes(lang) && text) {
      items.push({ lang, label: lang, text: String(text) });
    }
  });
  return items;
}

/**
 * 层级路径 + 每级多语言的 Popover 内容（供 RegionDisplay、RegionSelect 复用）
 */
export function RegionPathPopoverContent({
  pathNodes,
}: {
  pathNodes: API.LocationTreeNode[];
}) {
  const intl = useIntl();
  const levelLabels = [
    intl.formatMessage({ id: '组件.地区与字典.一级' }),
    intl.formatMessage({ id: '组件.地区与字典.二级' }),
    intl.formatMessage({ id: '组件.地区与字典.三级' }),
  ];
  const langLabels: Record<string, string> = {
    zh_CN: intl.formatMessage({ id: '通用.中文' }),
    en_US: intl.formatMessage({ id: '通用.英文' }),
    km_KH: intl.formatMessage({ id: '通用.高棉语' }),
  };
  if (!pathNodes.length) return null;
  return (
    <div style={{ minWidth: 240, maxWidth: 360 }}>
      {pathNodes.map((node, index) => {
        const isCurrent = index === pathNodes.length - 1;
        const levelLabel =
          index < 3
            ? levelLabels[index]
            : intl.formatMessage({ id: '组件.地区与字典.第N级' }, { n: index + 1 });
        const i18nItems = formatDisplayTextItems(node.display_text, langLabels);
        const levelDisplayName = getLocationDisplayName(node);
        return (
          <div
            key={node.id}
            style={{
              marginBottom: index < pathNodes.length - 1 ? 12 : 0,
              padding: isCurrent ? 8 : 0,
              paddingLeft: isCurrent ? 10 : 0,
              marginLeft: isCurrent ? -8 : 0,
              borderRadius: 4,
              borderLeft: isCurrent ? '3px solid #1890ff' : 'none',
              background: isCurrent ? '#e6f7ff' : undefined,
            }}
          >
            <div
              style={{
                fontSize: 12,
                color: isCurrent ? '#1890ff' : '#8c8c8c',
                marginBottom: 6,
                fontWeight: isCurrent ? 600 : 400,
              }}
            >
              {levelLabel} · {levelDisplayName}
              {isCurrent && (
                <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.9 }}>
                  {intl.formatMessage({ id: '组件.地区与字典.当前' })}
                </span>
              )}
            </div>
            {i18nItems.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {i18nItems.map((it) => (
                  <div
                    key={it.lang}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8,
                      fontSize: 13,
                    }}
                  >
                    <span style={{ minWidth: 70, color: '#8c8c8c', flexShrink: 0 }}>
                      {it.label}:
                    </span>
                    <span style={{ flex: 1, color: '#262626', wordBreak: 'break-word' }}>
                      {it.text}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: '#bfbfbf' }}>
                {intl.formatMessage({ id: '组件.地区与字典.暂无多语言' })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * 地区展示组件
 *
 * 用于展示地区（已启用国家的地区数据池），机制与字典项展示一致：
 * - 回显基于地区 id
 * - 展示名称：优先 display_text.zh_CN，其次 en_US，否则 name_en
 * - hover 时按层级展示：每一级单独展示其多语言（层级路径中每级都带 zh_CN / en_US / km_KH 等）
 */
const RegionDisplay: React.FC<RegionDisplayProps> = ({
  locationCodes,
  emptyText = '-',
  spaceSize = [0, 8],
  wrap = true,
}) => {
  const intl = useIntl();
  const [data, setData] = useState<Awaited<ReturnType<typeof getEnabledCountryLocationData>> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getEnabledCountryLocationData()
      .then(setData)
      .finally(() => setLoading(false));
  }, []);

  if (!locationCodes || (Array.isArray(locationCodes) && locationCodes.length === 0)) {
    return <span>{emptyText}</span>;
  }

  const codes = Array.isArray(locationCodes) ? locationCodes : [locationCodes];
  const mapByCode = data?.mapByCode ?? new Map();

  if (loading) {
    return (
      <Space size={spaceSize} wrap={wrap}>
        {codes.map((code) => (
          <Tag key={code}>{intl.formatMessage({ id: '通用.加载中' })}</Tag>
        ))}
      </Space>
    );
  }

  return (
    <Space size={spaceSize} wrap={wrap}>
      {codes.map((code) => {
        const item = mapByCode.get(code);
        const isFallback = !item;
        const pathNodes = item ? [...item.parentChain, item.node] : [];
        const displayValue = item ? getLocationDisplayName(item.node) : code;
        const hasPathContent = pathNodes.length > 0;

        const tagStyle: React.CSSProperties = {
          margin: 0,
          padding: '2px 10px',
          borderRadius: '4px',
          border: '1px solid #d9d9d9',
          backgroundColor: '#f5f5f5',
          color: '#595959',
          fontSize: '13px',
          lineHeight: '22px',
          cursor: hasPathContent ? 'help' : 'default',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'all 0.2s ease',
        };

        const tagContent = (
          <Tag
            key={code}
            style={tagStyle}
            color={isFallback ? 'warning' : undefined}
            onMouseEnter={(e) => {
              if (!isFallback) {
                const el = e.currentTarget;
                el.style.borderColor = '#1890ff';
                el.style.backgroundColor = '#e6f7ff';
                el.style.color = '#1890ff';
              }
            }}
            onMouseLeave={(e) => {
              if (!isFallback) {
                const el = e.currentTarget;
                el.style.borderColor = '#d9d9d9';
                el.style.backgroundColor = '#f5f5f5';
                el.style.color = '#595959';
              }
            }}
          >
            {!isFallback && hasPathContent && (
              <GlobalOutlined style={{ fontSize: 12, color: '#1890ff' }} />
            )}
            {isFallback && <WarningOutlined style={{ marginRight: 4, fontSize: 12 }} />}
            <span>{displayValue}</span>
          </Tag>
        );

        const hasHoverContent = !isFallback && hasPathContent;
        if (hasHoverContent) {
          return (
            <Popover
              key={code}
              trigger="hover"
              placement="top"
              overlayStyle={{ maxWidth: 420 }}
              mouseEnterDelay={0.3}
              mouseLeaveDelay={0.1}
              content={<RegionPathPopoverContent pathNodes={pathNodes} />}
            >
              {tagContent}
            </Popover>
          );
        }

        return (
          <Tooltip
            key={code}
            title={
              isFallback
                ? intl.formatMessage({ id: '组件.地区与字典.地区不存在显示原始code' }, { code })
                : undefined
            }
          >
            {tagContent}
          </Tooltip>
        );
      })}
    </Space>
  );
};

export default RegionDisplay;
